import { SensorData } from '@gozoki/api';
import { Chip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo } from 'react';
import SimpleDataGrid from '../../components/containers/SimpleDataGrid';

interface SensorGridRow {
    id: number;
    fridgeLabel: string;
    model: string | null;
    sensorRef: string | null;
    storeLabel: string;
    lastReading: string | null;
    temperature: number | null;
    temperatureThresholdMin: number;
    temperatureThresholdMax: number;
    humidity: number | null;
    battery: number | null;
    status: 'normal' | 'alert' | 'warning';
}

const sensorColumnDef = (): GridColDef[] => {
    const gridColumns: GridColDef[] = [
        {
            field: 'sensorRef',
            headerName: 'Référence',
            width: 120,
            valueFormatter: (params) => {
                if (!params.value || params.value === '') return '-';
                return params.value;
            },
        },
        {
            field: 'model',
            headerName: 'Modèle',
            width: 180,
            valueFormatter: (params) => {
                if (!params.value || params.value === '') return '-';
                return params.value;
            },
        },
        {
            field: 'fridgeLabel',
            headerName: 'Réfrigérateur',
            width: 180,
        },
        {
            field: 'storeLabel',
            headerName: 'Boutique',
            width: 180,
        },
        {
            field: 'lastReading',
            headerName: 'Dernier relevé',
            width: 180,
            valueFormatter: (params) => {
                if (!params.value) return '-';
                const date = new Date(params.value as string);
                return `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR')}`;
            },
        },
        {
            field: 'temperature',
            headerName: 'Température',
            width: 120,
            renderCell: (params) => {
                const temp = params.value as number | null;
                if (temp === null) return '-';
                const min = params.row.temperatureThresholdMin;
                const max = params.row.temperatureThresholdMax;
                const isAlert = temp < min || temp > max;
                return (
                    <div>
                        <div
                            style={{
                                fontWeight: 'bold',
                                color: isAlert ? '#d32f2f' : '#000',
                            }}
                        >
                            {temp > 0 ? '+' : ''}
                            {temp.toFixed(1)}°C
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            Seuil: {min}-{max}°C
                        </div>
                    </div>
                );
            },
        },
        {
            field: 'humidity',
            headerName: 'Humidité',
            width: 120,
            valueFormatter: (params) => {
                if (params.value === null) return '-';
                return `${params.value}%`;
            },
        },
        {
            field: 'battery',
            headerName: 'Batterie',
            width: 120,
            renderCell: (params) => {
                const battery = params.value as number | null;
                if (battery === null) return '-';
                let color = '#2e7d32';
                if (battery < 20) {
                    color = '#d32f2f';
                } else if (battery < 50) {
                    color = '#ff9800';
                }
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                            style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: color,
                            }}
                        />
                        {battery}%
                    </div>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Statut',
            width: 140,
            renderCell: (params) => {
                const status = params.value as string;
                let color: 'success' | 'error' | 'warning' = 'success';
                let label = 'Normal';

                if (status === 'alert') {
                    color = 'error';
                    label = 'Alerte';
                } else if (status === 'warning') {
                    color = 'warning';
                    label = 'Attention';
                }

                return <Chip label={label} color={color} style={{ height: 35 }} />;
            },
        },
    ];
    return gridColumns;
};

interface SensorGridProps {
    data?: SensorData[];
    loading?: boolean;
    onSelectionChange?: (selectedIds: number[]) => void;
    onPageChange?: (pageSensorIds: number[]) => void;
}

const SensorGrid = ({
    data = [],
    loading = false,
    onSelectionChange,
    onPageChange,
}: SensorGridProps) => {
    // Transform the API data to match the grid structure
    const rows: SensorGridRow[] = data.map((sensor) => {
        const temp = sensor.latestReading?.temperature ?? null;
        const min = 0; // TODO: Add thresholds to Fridge model
        const max = 6; // TODO: Add thresholds to Fridge model

        let status: 'normal' | 'alert' | 'warning' = 'normal';
        if (temp !== null && (temp < min || temp > max)) {
            status = 'alert';
        }

        return {
            id: sensor.id,
            fridgeLabel: sensor.fridge?.label || sensor.label,
            model: sensor.model || null,
            sensorRef: sensor.reference || null,
            storeLabel: sensor.storeLabel,
            lastReading: sensor.latestReading?.readingTime || null,
            temperature: temp,
            temperatureThresholdMin: min,
            temperatureThresholdMax: max,
            humidity: sensor.latestReading?.humidityPercentage ?? null,
            battery: sensor.latestReading?.sensorBatteryPercentage ?? null,
            status,
        };
    });

    // Get current page sensor IDs
    const currentPageSensorIds = useMemo(() => rows.map((row) => row.id), [rows]);

    // Notify parent of current page sensor IDs
    useEffect(() => {
        if (onPageChange) {
            onPageChange(currentPageSensorIds);
        }
    }, [currentPageSensorIds, onPageChange]);

    return (
        <SimpleDataGrid
            columns={sensorColumnDef()}
            rows={rows}
            loading={loading}
            disableRowSelectionOnClick
            checkboxSelection
            onRowSelectionModelChange={(selection) => {
                if (onSelectionChange) {
                    onSelectionChange(selection as number[]);
                }
            }}
        />
    );
};

export default SensorGrid;
